import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@fastnorth/i18next';
import ProportionBar from '@fastnorth/kraken/components/proportion-bar';
import { Div, Flex } from '@fastnorth/kraken/components/layout';
import { H3, Text } from '@fastnorth/kraken/components/typography';
import type { SubEventProgressOverview } from '../types';
import formatProgressCount from '../utils/formatProgressCount';
import formatProgressPercent from '../utils/formatProgressPercent';
import scrollToSubEventProgressSection from '../utils/scrollToSubEventProgressSection';
import RaceSummaryOnCourseHelp from './RaceSummaryOnCourseHelp';
import RaceSummaryStatSwatch from './RaceSummaryStatSwatch';
import {
  raceSummaryCardVariants,
  raceSummaryFootnoteVariants,
  raceSummaryProportionBarSpacingVariants,
  raceSummaryStatCounterVariants,
  raceSummaryStatLabelVariants,
} from './RaceSummaryCard.styles';

export type RaceSummaryCardProps = {
  subEvent: SubEventProgressOverview;
};

const RaceSummaryCard = ({ subEvent }: RaceSummaryCardProps): ReactElement => {
  const { i18n, t } = useTranslation('translation', { keyPrefix: 'karwa.event_progress_v2' });
  const locale = i18n.language as Locale;

  const onCoursePercentage: number =
    subEvent.startedCount > 0 ? (subEvent.onCoursePercentage ?? 0) : 0;
  const finishedPercentage: number =
    subEvent.startedCount > 0 ? (subEvent.finishedPercentage ?? 0) : 0;

  const handleActivate = (): void => {
    scrollToSubEventProgressSection(subEvent.subEventId);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  return (
    <Div
      className={`${raceSummaryCardVariants()} group`}
      data-testid={`race-summary-card-${subEvent.subEventId}`}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}>
      <H3 fontWeight="semibold" size="lg" spacing="0 0 4">
        {subEvent.name}
      </H3>
      <Div className={raceSummaryProportionBarSpacingVariants()}>
        <ProportionBar segments={subEvent.proportionSegments} />
      </Div>
      <Flex className="w-full min-w-0 flex-col gap-5 sm:flex-row sm:gap-0">
        <Div className="flex min-w-0 flex-1 flex-col gap-4 sm:pr-4">
          <Text className={raceSummaryStatLabelVariants({ display: 'block' })}>
            {t('participants_stat_label')}
          </Text>
          <Text className={raceSummaryStatCounterVariants({ tone: 'participants' })}>
            {formatProgressCount(subEvent.expectedAtFinishCount, locale)}
          </Text>
          <Text color="muted" size="sm">
            {t('expected_at_finish_label')}
          </Text>
        </Div>
        <Div className="flex min-w-0 flex-1 flex-col gap-4 sm:border-l sm:border-border sm:pl-6 sm:pr-3">
          <Flex className="items-center">
            <Div className="mr-4 flex shrink-0 items-center self-center">
              <RaceSummaryStatSwatch variant="onCourse" />
            </Div>
            <Flex className="min-w-0 items-center gap-3">
              <Text className={raceSummaryStatLabelVariants()}>{t('on_course_stat_label')}</Text>
              <RaceSummaryOnCourseHelp />
            </Flex>
          </Flex>
          <Text className={raceSummaryStatCounterVariants({ tone: 'onCourse' })}>
            {formatProgressCount(subEvent.onCourseCount, locale)}
          </Text>
          <Text color="muted" size="sm">
            {formatProgressPercent(onCoursePercentage, locale)}
          </Text>
        </Div>
        <Div className="flex min-w-0 flex-1 flex-col gap-4 sm:border-l sm:border-border sm:pl-6 sm:pr-0">
          <Flex className="items-center">
            <Div className="mr-4 flex shrink-0 items-center self-center">
              <RaceSummaryStatSwatch variant="finished" />
            </Div>
            <Text className={raceSummaryStatLabelVariants()}>{t('finished_stat_label')}</Text>
          </Flex>
          <Text className={raceSummaryStatCounterVariants({ tone: 'finished' })}>
            {formatProgressCount(subEvent.finishedCount, locale)}
          </Text>
          <Text color="muted" size="sm">
            {formatProgressPercent(finishedPercentage, locale)}
          </Text>
        </Div>
      </Flex>
      <span className={raceSummaryFootnoteVariants()}>
        {t('summary_footnote_dnf_dns_dq', {
          dnf: formatProgressCount(subEvent.dnfCount, locale),
          dns: formatProgressCount(subEvent.dnsCount, locale),
          dq: formatProgressCount(subEvent.dqCount, locale),
          dqLabel: t('dq_label'),
        })}
      </span>
    </Div>
  );
};

export default RaceSummaryCard;
